import { store } from "../../store";
import eventBus from "../../eventBus";
import { ApiClient } from "../../api/ApiClient";
import { enqueueItem, SyncItem, SyncItemType } from "./SyncActions";
import { CUSTODIAN_CASHFLOW_API_KEYS, FUND_SCHEDULE_API_KEYS } from "./CustodianSyncApiKeys";
import {
  converUIDateFormatToCustodianHistoryApiDateFormat,
  getCustodianHistoryApiFormattedDateString,
  getDateInKuberaFormat,
  getUuid,
  guessDateInCustodianHistoryUIFormat,
  getCustodianHistoryFormattedDateString,
  parseKuberaDateString
} from "../../utilities/Number";
import {
  getTickersForIds,
  addTickerInfoAction,
  getTickersForText,
  convertPVSTRateToValueExchangeRate
} from "./TickerActions";
import {
  copyLinkingInfoFromParentCustodian,
  getNetWorthChartStartDateForPortfolio
} from "../reducers/PortfolioReducer";
import {
  custodianDetailsSelector,
  fundScheduleSelector,
  getCustodianChartDataFromCache,
  getCustodianDetailsFromCache,
  removeCustodianDetailsFromCache,
  saveCustodianChartDataToCache,
  saveCustodianDetailsToCache
} from "../reducers/CustodianDetailsReducer";
import { syncQueueSelector, isCustodianUpdateOngoingSelector } from "../reducers/SyncReducer";
import {
  updateCustodianInBulkAction,
  insertCustodianInBulkAction,
  updateCustodian,
  setSectionUpdated,
  updateSectionIrr,
  getIrrValue,
  updateCustodianLocal,
  fetchPortfolioChangeData,
  applyChangeDataDeltas
} from "./PortfolioActions";
import {
  irrTypes,
  isCostBasisIrrTypeWithoutCost,
  getChartDataFromResponse,
  getExchangeRateDetails,
  updateDashboardAction,
  toastType,
  RESET_CUSTODIAN_DETAILS,
  FETCH_CUSTODIAN_DETAILS,
  FETCH_CUSTODIAN_DETAILS_SUCCESS,
  FETCH_CUSTODIAN_DETAILS_HISTORY_SUCCESS,
  SET_CURRENT_STATE,
  SET_INNER_DETAILS_STATE,
  FETCH_CUSTODIAN_DETAILS_ERROR,
  UPDATE_CUSTODIAN_HISTORY,
  REFRESH_CUSTODIAN_HISTORY,
  UPDATE_CUSTODIAN_CASHFLOW,
  DELETE_CUSTODIAN_CASHFLOW,
  ADD_PENDING_IRR_REQUEST,
  REMOVE_PENDING_IRR_REQUEST,
  filterChartDataBasedOnPortfolioStartDate,
  getYTDCustodianChartData,
  updateCustodianDetailsAction,
  DELETE_CUSTODIAN_HISTORY,
  fundScheduleTypes,
  UPDATE_FUND_SCHEDULE,
  DELETE_FUND_SCHEDULE,
  GET_CUSTODIAN_CHART,
  GET_CUSTODIAN_CHART_SUCCESS,
  GET_CUSTODIAN_CHART_ERROR
} from "./Common";
import {
  currentPortfolioSelector,
  custodianSelector,
  portfoliosStateSelector,
  chartTimeRange,
  getExchangeRate,
  getTickerUsingId,
  getTickerUsingShortName,
  getCustodianValueById,
  getCustodianAdjustmentValue,
  getAdjustmentEntryInEffect,
  linkedPortfolioCustodianInCategorySelector,
  UNKNOWN_TICKER_SHORT_NAME,
  PVST_VALUE_TICKER_ID,
  categoryType,
  accountLinkingService
} from "../reducers/Common";
import { apiErrorCodes } from "../../api/ApiResponse";
import { Toast, showToastAction } from "./ToastActions";
import i18n from "i18next";
import { getHashParams, isMobile } from "../../utilities";
import DeferredPromise from "utilities/DeferredPromise";
import { toUserTimezoneDate, getTodayCivilDateString } from "../../utilities/Timezone";

const isMobileDevice = isMobile();

export const fetchCustodianDetailsAction = () => ({
  type: FETCH_CUSTODIAN_DETAILS
});

export const fetchCustodianDetailsSuccessAction = (details, linkedSiblingDetails) => ({
  type: FETCH_CUSTODIAN_DETAILS_SUCCESS,
  details,
  linkedSiblingDetails
});

export const fetchCustodianDetailsHistorySuccessAction = (history, isCostHistory, clearPreviousHistory) => ({
  type: FETCH_CUSTODIAN_DETAILS_HISTORY_SUCCESS,
  history,
  isCostHistory,
  clearPreviousHistory
});

export const getCustodianChartAction = () => ({
  type: GET_CUSTODIAN_CHART
});

export const getCustodianChartSuccessAction = chartData => ({
  type: GET_CUSTODIAN_CHART_SUCCESS,
  chartData
});

export const getCustodianChartErrorAction = error => ({
  type: GET_CUSTODIAN_CHART_ERROR,
  error
});

export const setCurrentStateAction = state => ({
  type: SET_CURRENT_STATE,
  state
});

export const setInnerDetailsStateAction = scrollTop => ({
  type: SET_INNER_DETAILS_STATE,
  scrollTop
});

export const fetchCustodianDetailsErrorAction = error => ({
  type: FETCH_CUSTODIAN_DETAILS_ERROR,
  error
});

export const resetCustodianDetailsAction = () => ({
  type: RESET_CUSTODIAN_DETAILS
});

export const deleteCustodianHistoryAction = (historyId, isCostHistory) => ({
  type: DELETE_CUSTODIAN_HISTORY,
  historyId,
  isCostHistory
});

export const updateCustodianHistoryAction = (history, isCostHistory) => ({
  type: UPDATE_CUSTODIAN_HISTORY,
  history,
  isCostHistory
});

export const refreshCustodianHistoryAction = isRefreshing => ({
  type: REFRESH_CUSTODIAN_HISTORY,
  isRefreshing
});

export const updateCustodianCashflowAction = cashflow => ({
  type: UPDATE_CUSTODIAN_CASHFLOW,
  cashflow
});

export const deleteCustodianCashflowAction = cashflowId => ({
  type: DELETE_CUSTODIAN_CASHFLOW,
  cashflowId
});

export const addPendingIrrRequestAction = requestId => ({
  type: ADD_PENDING_IRR_REQUEST,
  requestId
});

export const removePendingIrrRequestAction = requestId => ({
  type: REMOVE_PENDING_IRR_REQUEST,
  requestId
});

export const updateFundScheduleAction = fundSchedule => ({
  type: UPDATE_FUND_SCHEDULE,
  fundSchedule
});

export const deleteFundScheduleAction = id => ({
  type: DELETE_FUND_SCHEDULE,
  id
});

export const showCopiedToast = (message = i18n.t("numberCopied")) => {
  return dispatch => {
    const toast = new Toast(toastType.TIP, message, 1500, () => {}, () => {});
    dispatch(showToastAction(toast));
  };
};

export const markHoldingsToast = markedAsInvestable => {
  return dispatch => {
    const toastMessage = markedAsInvestable
      ? i18n.t("markHoldings.toast.investable")
      : i18n.t("markHoldings.toast.nonInvestable");
    const toast = new Toast(toastType.TIP, toastMessage, 1500, () => {}, () => {});
    dispatch(showToastAction(toast));
  };
};

export const copyCustodianHistoryAfterDate = (sectionId, custodianId, properties, onSuccess) => {
  return dispatch => {
    ApiClient.copyCustodianHistoryAfterDate(getUuid(), sectionId, custodianId, properties)
      .then(apiData => {
        const history = apiData.payload.history;
        dispatch(fetchCustodianDetailsHistorySuccessAction(history, false, true));

        setTimeout(() => {
          onSuccess(apiData.payload.info);
          dispatch(refreshCustodianHistoryAction(false));
        }, 0);
      })
      .catch(apiError => {
        dispatch(refreshCustodianHistoryAction(false));
      });
  };
};

const preFetchCustodianDetailsInFlightMap = new Map();
const lastPreFetchCustodianDetailsTimeMap = new Map();
export const preFetchCustodianDetails = custodianId => {
  return dispatch => {
    if (lastPreFetchCustodianDetailsTimeMap.has(custodianId)) {
      const lastPreFetchCustodianDetailsTime = lastPreFetchCustodianDetailsTimeMap.get(custodianId);
      if (new Date().getTime() - lastPreFetchCustodianDetailsTime < 60 * 1000) {
        return;
      }
    }
    lastPreFetchCustodianDetailsTimeMap.set(custodianId, new Date().getTime());
    dispatch(fetchCustodianDetails(custodianId, undefined, undefined, undefined, undefined, true));
    dispatch(getCustodianChart(custodianId, undefined, undefined, undefined, true));
  };
};

export const fetchCustodianDetails = (
  custodianId,
  updateCustodian = false,
  matchIdWithUrlHashParam = true,
  otherParams = {},
  fetchFromCache = false,
  preFetchOnly = false
) => {
  return (dispatch, getState) => {
    if (!preFetchOnly) {
      dispatch(fetchCustodianDetailsAction());
    }

    const maxRetryCount = 3;
    var retryCount = 0;
    const fetchDetails = (cachedApiData = null) => {
      const custodian = custodianSelector(getState(), custodianId);
      let promises = [];
      if (cachedApiData) {
        promises = [
          new Promise(resolve => {
            resolve(cachedApiData.detailsApiData);
          }),
          new Promise(resolve => {
            resolve(cachedApiData.linkedSiblingDetailsApiData);
          })
        ];
      } else {
        if (!preFetchOnly && preFetchCustodianDetailsInFlightMap.has(custodianId)) {
          promises = preFetchCustodianDetailsInFlightMap.get(custodianId);
        } else {
          promises = [ApiClient.getCustodianDetails(getUuid(), custodianId)];
          if (custodian?.linkType === accountLinkingService.KUBERA_PORTFOLIO && !isMobileDevice) {
            const otherCategory = (custodian.linkContainer === categoryType.ASSET.toLowerCase()
              ? categoryType.DEBT
              : categoryType.ASSET
            ).toLowerCase();
            const linkedCustodianInAnotherCategory = linkedPortfolioCustodianInCategorySelector(
              getState(),
              otherCategory,
              custodian.linkProviderAccountId
            );

            if (linkedCustodianInAnotherCategory?.id) {
              promises.push(ApiClient.getCustodianDetails(getUuid(), linkedCustodianInAnotherCategory.id));
            }
          }
        }
      }

      if (preFetchOnly) {
        preFetchCustodianDetailsInFlightMap.set(custodianId, promises);
      }

      Promise.all(promises)
        .then(([detailsApiData, linkedDetailsApiData]) => {
          if (!cachedApiData) {
            saveCustodianDetailsToCache(custodianId, detailsApiData, linkedDetailsApiData);
          }

          if (preFetchOnly) {
            preFetchCustodianDetailsInFlightMap.delete(custodianId);
            return;
          }

          const detailsData = detailsApiData.payload;
          const linkedSiblingDetails = linkedDetailsApiData?.payload;

          detailsData.isHistoryLoaded = detailsData.history.length === 0;

          if (updateCustodian && !otherParams.preventUpdateCustodianLocal) {
            dispatch(updateCustodianLocal(detailsData.info));
          }

          if (!detailsData.info.parentId === false) {
            const portfoliosState = portfoliosStateSelector(getState());
            copyLinkingInfoFromParentCustodian(portfoliosState, detailsData.info.parentId, detailsData.info);
          }

          if (isCostBasisIrrTypeWithoutCost(detailsData.info)) {
            detailsData.info.irrType = irrTypes.CASHFLOW;
          }

          detailsData.costHistory = detailsData.costBasis || [];
          detailsData.isCostHistoryLoaded = detailsData.costHistory.length === 0;

          const getUnknownTickerIds = () => {
            var unknownTickerIds = new Set();
            var tickerIdsToTest = new Set();

            if (!detailsData.info.valueTickerId === false) {
              tickerIdsToTest.add(detailsData.info.valueTickerId);
            }
            if (!detailsData.info.costTickerId === false) {
              tickerIdsToTest.add(detailsData.info.costTickerId);
            }

            for (const holding of detailsData.holdings) {
              if (!holding.valueTickerId === false) {
                tickerIdsToTest.add(holding.valueTickerId);
              }
              if (!holding.costTickerId === false) {
                tickerIdsToTest.add(holding.costTickerId);
              }
            }
            const currentPortfolio = currentPortfolioSelector(getState());
            dispatch(insertCustodianInBulkAction(currentPortfolio.id, detailsData.holdings));

            for (const history of detailsData.history) {
              if (!history.valueTickerId === false) {
                tickerIdsToTest.add(history.valueTickerId);
              }
            }

            for (const cashflow of detailsData.cashFlow) {
              if (!cashflow.cashInTickerId === false) {
                tickerIdsToTest.add(cashflow.cashInTickerId);
              }
              if (!cashflow.cashOutTickerId === false) {
                tickerIdsToTest.add(cashflow.cashOutTickerId);
              }
            }

            for (const tickerId of Array.from(tickerIdsToTest)) {
              if (getTickerUsingId(getState(), tickerId).shortName === UNKNOWN_TICKER_SHORT_NAME) {
                unknownTickerIds.add(tickerId);
              }
            }
            return Array.from(unknownTickerIds);
          };

          const unknownTickerIds = getUnknownTickerIds();
          if (unknownTickerIds.length > 0) {
            dispatch(
              getTickersForIds(
                unknownTickerIds,
                () => {
                  const urlHashParams = getHashParams(window.location);
                  const id = urlHashParams["id"];
                  if (id === custodianId) {
                    dispatch(fetchCustodianDetailsSuccessAction(detailsData, linkedSiblingDetails));
                  }
                },
                apiError => {
                  dispatch(fetchCustodianDetailsErrorAction(apiError));
                }
              )
            );
          } else if (matchIdWithUrlHashParam === true) {
            const urlHashParams = getHashParams(window.location);
            const id = urlHashParams["id"];
            if (id === custodianId) {
              dispatch(fetchCustodianDetailsSuccessAction(detailsData, linkedSiblingDetails));
            }
          } else {
            dispatch(fetchCustodianDetailsSuccessAction(detailsData, linkedSiblingDetails));
          }
        })
        .catch(apiError => {
          if (preFetchOnly) {
            preFetchCustodianDetailsInFlightMap.delete(custodianId);
          }
          removeCustodianDetailsFromCache(custodianId);

          if (apiError.errorCode === apiErrorCodes.INVALID_INPUT && retryCount < maxRetryCount) {
            retryCount++;

            setTimeout(() => {
              fetchDetails();
            }, 3000);
          } else {
            dispatch(fetchCustodianDetailsErrorAction(apiError));
          }
        });
    };

    if (fetchFromCache) {
      const cachedApiData = getCustodianDetailsFromCache(custodianId);
      if (cachedApiData) {
        fetchDetails(cachedApiData);
      }
    }
    fetchDetails();
  };
};

export const fetchCustodianDetailsHistory = (
  custodianId,
  timeStamp,
  isCostHistory = false,
  clearPreviousHistory = false,
  onCompletion = null
) => {
  return dispatch => {
    dispatch(fetchCustodianDetailsAction());

    ApiClient.getCustodianDetailsHistory(getUuid(), custodianId, timeStamp, isCostHistory)
      .then(apiData => {
        const history = isCostHistory ? apiData.payload.costBasis : apiData.payload.history;
        dispatch(fetchCustodianDetailsHistorySuccessAction(history, isCostHistory, clearPreviousHistory));

        if (onCompletion) {
          onCompletion();
        }
      })
      .catch(apiError => {
        dispatch(fetchCustodianDetailsErrorAction(apiError));

        if (onCompletion) {
          onCompletion();
        }
      });
  };
};

export const deleteCustodianHistory = (sectionId, custodianId, historyId, isCostHistory = false, onCompletion) => {
  return dispatch => {
    dispatch(deleteCustodianHistoryAction(historyId, isCostHistory));

    const request = idempotentId => ApiClient.deleteCustodianHistory(idempotentId, sectionId, custodianId, historyId);
    const syncItem = new SyncItem(SyncItemType.DELETE, history.id, request, 0, false, apiData => {
      if (onCompletion) {
        onCompletion();
      }
    });
    dispatch(enqueueItem(syncItem));
  };
};

export const updateCustodianHistory = (
  isFirstEdit,
  sectionId,
  custodianId,
  history,
  isCostHistory = false,
  onCompletion = null
) => {
  return (dispatch, getState) => {
    if (history) {
      history.date = converUIDateFormatToCustodianHistoryApiDateFormat(history.date);

      if (!history.date) {
        return;
      }

      const apiKeys = [
        "id",
        "date",
        "cost",
        "value",
        "costTickerId",
        "costExchangeRate",
        "valueTickerId",
        "valueExchangeRate",
        "rate"
      ];

      const apiObject = Object.keys(history)
        .filter(key => apiKeys.includes(key) && history[key] !== null)
        .reduce((obj, key) => {
          obj[key] = history[key];
          return obj;
        }, {});

      // While updating a custodian if value field is non-null ensure that the valueTickerId
      // and valueExchangeRate fields are also set correctly
      if (history.value != null) {
        if (history.valueTickerId === PVST_VALUE_TICKER_ID && history.rate) {
          apiObject.valueExchangeRate = dispatch(
            convertPVSTRateToValueExchangeRate(
              history.rate,
              guessDateInCustodianHistoryUIFormat(history.date).dateString,
              false
            )
          );
        }
      }

      dispatch(updateCustodianHistoryAction(apiObject, isCostHistory));

      const portfolioIdForChangeData = currentPortfolioSelector(getState())?.id;

      if (isFirstEdit === true) {
        const request = idempotentId =>
          ApiClient.createCustodianHistory(idempotentId, sectionId, custodianId, apiObject);
        const syncItem = new SyncItem(SyncItemType.CREATE, history.id, request, 0, false, apiData => {
          if (portfolioIdForChangeData) {
            dispatch(fetchPortfolioChangeData(portfolioIdForChangeData, false));
          }
          if (onCompletion) {
            onCompletion();
          }
        });
        dispatch(enqueueItem(syncItem));
      } else {
        const request = idempotentId =>
          ApiClient.updateCustodianHistory(idempotentId, sectionId, custodianId, apiObject);
        const syncItem = new SyncItem(SyncItemType.UPDATE, history.id, request, 1000, true, apiData => {
          if (portfolioIdForChangeData) {
            dispatch(fetchPortfolioChangeData(portfolioIdForChangeData, false));
          }
          if (onCompletion) {
            onCompletion();
          }
        });
        dispatch(enqueueItem(syncItem));
      }
    }
  };
};

export const deleteOwnershipHistoryForCustodian = (sectionId, custodianId, ownershipId) => {
  return (dispatch, getState) => {
    if (!sectionId || !custodianId || !ownershipId) {
      return;
    }

    const custodian = custodianSelector(getState(), custodianId);
    if (!custodian.parentId === false) {
      const parentCustodian = custodianSelector(getState(), custodian.parentId);
      custodianId = parentCustodian.id;
      sectionId = parentCustodian.sectionId;
    }

    const request = idempotentId =>
      ApiClient.deleteCustodianOwnership(idempotentId, sectionId, custodianId, ownershipId);
    const syncItem = new SyncItem(SyncItemType.DELETE, ownershipId, request, 0, false, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

export const updateOwnershipHistoryForCustodian = (
  isNewEntry,
  sectionId,
  custodianId,
  ownershipEntry,
  onCompletion = null
) => {
  return (dispatch, getState) => {
    if (
      !custodianId ||
      !ownershipEntry ||
      !ownershipEntry.date ||
      (!ownershipEntry.percentage && ownershipEntry.percentage !== 0) ||
      !ownershipEntry.id
    ) {
      return;
    }

    const currentPortfolio = currentPortfolioSelector(getState());
    const custodian = custodianSelector(getState(), custodianId);
    if (!custodian.parentId === false) {
      const parentCustodian = custodianSelector(getState(), custodian.parentId);
      custodianId = parentCustodian.id;
      sectionId = parentCustodian.sectionId;
    }

    const handlePayload = payload => {
      const updatedCustodian = payload.info;
      const custodian = custodianSelector(getState(), updatedCustodian.id);
      if (getIrrValue(custodian?.irr, true) !== getIrrValue(updatedCustodian.irr, true)) {
        dispatch(updateSectionIrr([updatedCustodian.sectionId]));
      }

      dispatch(updateCustodianInBulkAction(currentPortfolio.id, [updatedCustodian]));

      payload.holdings?.forEach(holding => {
        dispatch(updateCustodianHolding(holding, holding));
      });
    };

    if (isNewEntry === true) {
      const request = idempotentId =>
        ApiClient.createCustodianOwnership(idempotentId, sectionId, custodianId, ownershipEntry);
      const syncItem = new SyncItem(SyncItemType.CREATE, ownershipEntry.id, request, 1000, true, apiData => {
        if (onCompletion) {
          onCompletion();
        }
        handlePayload(apiData.payload);
      });
      dispatch(enqueueItem(syncItem));
    } else {
      const request = idempotentId =>
        ApiClient.updateCustodianOwnership(idempotentId, sectionId, custodianId, ownershipEntry.id, ownershipEntry);
      const syncItem = new SyncItem(SyncItemType.UPDATE, ownershipEntry.id, request, 1000, true, apiData => {
        if (onCompletion) {
          onCompletion();
        }
        handlePayload(apiData.payload);
      });
      dispatch(enqueueItem(syncItem));
    }
  };
};

const tagAdjustmentSyncItem = (syncItem, custodianId) => {
  syncItem.adjustmentCustodianId = custodianId;
  return syncItem;
};

const refreshAdjustmentHistoryForCustodian = custodianId => {
  return (dispatch, getState) => {
    return ApiClient.getCustodianAdjustment(getUuid(), custodianId)
      .then(apiData => {
        const state = getState();
        const details = custodianDetailsSelector(state);
        const hasPendingAdjustmentWrites =
          syncQueueSelector(state).some(item => item.adjustmentCustodianId === custodianId) ||
          isCustodianUpdateOngoingSelector(state, custodianId);
        if (details?.info?.id !== custodianId || hasPendingAdjustmentWrites) {
          return details?.adjustment;
        }
        const localIdsByDate = new Map(
          (details.adjustment || []).filter(entry => entry.isLocal === true).map(entry => [entry.date, entry.id])
        );
        const adjustment = (apiData.payload.adjustment || []).map(entry =>
          localIdsByDate.has(entry.date) ? { ...entry, localId: localIdsByDate.get(entry.date) } : entry
        );
        dispatch(updateCustodianDetailsAction({ ...details, adjustment }));
        return adjustment;
      })
      .catch(() => custodianDetailsSelector(getState())?.adjustment);
  };
};

const updateAdjustedCustodian = (custodianId, getCustodianUpdateAction) => {
  return (dispatch, getState) => {
    const custodian = custodianSelector(getState(), custodianId);
    if (!custodian === true) {
      return;
    }
    const countedCustodianId =
      !custodian.parentId === false && custodian.hidden === 1 ? custodian.parentId : custodian.id;
    const valueBeforeUpdate = getCustodianValueById(getState(), countedCustodianId);
    dispatch(getCustodianUpdateAction(currentPortfolioSelector(getState()).id));
    dispatch(updateDashboardAction([...new Set([custodian.id, countedCustodianId])]));
    dispatch(
      applyChangeDataDeltas({
        [countedCustodianId]: getCustodianValueById(getState(), countedCustodianId) - valueBeforeUpdate
      })
    );
  };
};

const updateCustodianValueAdjustment = (custodianId, adjustmentProperties) => {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      if (!custodianSelector(getState(), custodianId) === true) {
        resolve(null);
        return;
      }
      dispatch(
        updateAdjustedCustodian(custodianId, () =>
          updateCustodian(false, custodianId, adjustmentProperties, false, resolve)
        )
      );
    });
  };
};

const handleAdjustmentPayload = (payload, syncItem) => {
  return (dispatch, getState) => {
    const updatedCustodian = payload?.info;
    if (!updatedCustodian === true) {
      return;
    }
    const hasOtherPendingAdjustmentWrites = syncQueueSelector(getState()).some(
      item => item !== syncItem && item.adjustmentCustodianId === updatedCustodian.id
    );
    if (hasOtherPendingAdjustmentWrites) {
      return;
    }
    const custodian = custodianSelector(getState(), updatedCustodian.id);
    if (!custodian === true) {
      return;
    }
    if (getIrrValue(custodian.irr, true) !== getIrrValue(updatedCustodian.irr, true)) {
      dispatch(updateSectionIrr([updatedCustodian.sectionId]));
    }
    dispatch(
      updateAdjustedCustodian(updatedCustodian.id, portfolioId =>
        updateCustodianInBulkAction(portfolioId, [updatedCustodian], true, null, ["adjValue", "adjTickerId", "irr"])
      )
    );
  };
};

const applyAdjustmentHistoryLocally = (custodianId, getUpdatedAdjustment) => {
  return (dispatch, getState) => {
    const details = custodianDetailsSelector(getState());
    if (details?.info?.id !== custodianId) {
      return null;
    }
    const adjustment = getUpdatedAdjustment([...(details.adjustment || [])]).sort((a, b) =>
      b.date.localeCompare(a.date)
    );
    dispatch(updateCustodianDetailsAction({ ...details, adjustment }));

    const custodian = custodianSelector(getState(), custodianId);
    const entryInEffect = getAdjustmentEntryInEffect(adjustment);
    const adjValue = !entryInEffect === true ? null : entryInEffect.value;
    if (!custodian === false && getCustodianAdjustmentValue(custodian) !== getCustodianAdjustmentValue({ adjValue })) {
      dispatch(
        updateAdjustedCustodian(custodianId, portfolioId =>
          updateCustodianInBulkAction(
            portfolioId,
            [{ id: custodianId, adjValue, adjTickerId: !entryInEffect === true ? null : entryInEffect.tickerId }],
            true,
            null,
            ["adjValue", "adjTickerId"]
          )
        )
      );
    }
    return adjustment;
  };
};

export const updateValueAdjustmentForCustodian = (custodianId, adjustmentProperties, onCompletion = null) => {
  return async dispatch => {
    dispatch(
      applyAdjustmentHistoryLocally(custodianId, adjustment => {
        const today = getTodayCivilDateString();
        const tsModified = Math.floor(Date.now() / 1000);
        const entryInEffect = getAdjustmentEntryInEffect(adjustment, today);
        if (adjustmentProperties.adjValue === undefined) {
          return adjustment.map(entry =>
            entry.id === entryInEffect?.id ? { ...entry, notes: adjustmentProperties.adjNotes, tsModified } : entry
          );
        }
        if (!entryInEffect === true && !parseFloat(adjustmentProperties.adjValue) === true) {
          return adjustment;
        }
        const todaysEntry = adjustment.find(entry => entry.date === today);
        return [
          ...adjustment.filter(entry => entry.date !== today),
          {
            ...(todaysEntry || { id: getUuid(), custodianId, date: today, isLocal: true }),
            value: adjustmentProperties.adjValue,
            tickerId: adjustmentProperties.adjTickerId,
            notes: adjustmentProperties.adjNotes,
            tsModified
          }
        ];
      })
    );
    await dispatch(updateCustodianValueAdjustment(custodianId, adjustmentProperties));
    await dispatch(refreshAdjustmentHistoryForCustodian(custodianId));
    if (onCompletion) {
      onCompletion();
    }
  };
};

export const updateAdjustmentHistoryForCustodian = (
  isNewEntry,
  sectionId,
  custodianId,
  adjustmentEntry,
  onCompletion = null
) => {
  return (dispatch, getState) => {
    if (
      !sectionId ||
      !custodianId ||
      !adjustmentEntry ||
      !adjustmentEntry.id ||
      !adjustmentEntry.date ||
      (!adjustmentEntry.value && adjustmentEntry.value !== 0)
    ) {
      return;
    }

    const apiEntry = {
      date: adjustmentEntry.date,
      value: adjustmentEntry.value,
      tickerId: adjustmentEntry.tickerId,
      notes: adjustmentEntry.notes
    };

    const handleResponse = async apiData => {
      dispatch(handleAdjustmentPayload(apiData.payload, syncItem));
      const adjustment = await dispatch(refreshAdjustmentHistoryForCustodian(custodianId));
      if (onCompletion) {
        onCompletion(adjustment);
      }
    };

    const existingLocalEntry = (custodianDetailsSelector(getState())?.adjustment || []).find(
      entry => entry.id === adjustmentEntry.id && entry.isLocal === true
    );
    if (isNewEntry === true && !existingLocalEntry === false && existingLocalEntry.date !== adjustmentEntry.date) {
      const deleteOldDateRequest = idempotentId =>
        ApiClient.deleteCustodianAdjustment(idempotentId, sectionId, custodianId, existingLocalEntry.date);
      dispatch(
        enqueueItem(
          tagAdjustmentSyncItem(
            new SyncItem(SyncItemType.DELETE, existingLocalEntry.date, deleteOldDateRequest, 0, false, () => null),
            custodianId
          )
        )
      );
    }

    const request =
      isNewEntry === true
        ? idempotentId => ApiClient.createCustodianAdjustment(idempotentId, sectionId, custodianId, apiEntry)
        : idempotentId =>
            ApiClient.updateCustodianAdjustment(idempotentId, sectionId, custodianId, adjustmentEntry.id, apiEntry);
    const syncItem = new SyncItem(
      isNewEntry === true ? SyncItemType.CREATE : SyncItemType.UPDATE,
      adjustmentEntry.id,
      request,
      1000,
      false,
      handleResponse
    );
    dispatch(enqueueItem(tagAdjustmentSyncItem(syncItem, custodianId)));

    dispatch(
      applyAdjustmentHistoryLocally(custodianId, adjustment => [
        ...adjustment.filter(entry => entry.id !== adjustmentEntry.id && entry.date !== adjustmentEntry.date),
        {
          ...adjustment.find(entry => entry.id === adjustmentEntry.id),
          ...adjustmentEntry,
          custodianId,
          isLocal: isNewEntry === true,
          tsModified: Math.floor(Date.now() / 1000)
        }
      ])
    );
  };
};

export const deleteAdjustmentHistoryForCustodian = (sectionId, custodianId, adjustmentId, onCompletion = null) => {
  return (dispatch, getState) => {
    if (!sectionId || !custodianId || !adjustmentId) {
      return;
    }

    const entryToDelete = (custodianDetailsSelector(getState())?.adjustment || []).find(
      entry => entry.id === adjustmentId || entry.localId === adjustmentId
    );
    if (!entryToDelete === true) {
      return;
    }

    const apiAdjustmentId = entryToDelete.isLocal === true ? entryToDelete.date : entryToDelete.id;
    const request = idempotentId =>
      ApiClient.deleteCustodianAdjustment(idempotentId, sectionId, custodianId, apiAdjustmentId);
    const syncItem = new SyncItem(SyncItemType.DELETE, apiAdjustmentId, request, 0, false, async apiData => {
      dispatch(handleAdjustmentPayload(apiData.payload, syncItem));
      const adjustment = await dispatch(refreshAdjustmentHistoryForCustodian(custodianId));
      if (onCompletion) {
        onCompletion(adjustment);
      }
    });
    dispatch(enqueueItem(tagAdjustmentSyncItem(syncItem, custodianId)));

    dispatch(
      applyAdjustmentHistoryLocally(custodianId, adjustment =>
        adjustment.filter(entry => entry.id !== entryToDelete.id)
      )
    );
  };
};

export const updateCostBasisHistoryForCustodian = (sectionId, custodianId, costBasisId, history) => {
  return dispatch => {
    if (history) {
      history.date = converUIDateFormatToCustodianHistoryApiDateFormat(history.date);

      if (!history.date) {
        return;
      }
      dispatch(updateCustodianHistoryAction(history, true));
      const request = idempotentId =>
        ApiClient.updateCostBasisHistoryForCustodian(idempotentId, sectionId, custodianId, costBasisId, history);
      const syncItem = new SyncItem(SyncItemType.UPDATE, history.id, request, 1000, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    }
  };
};

export const deleteCostBasisHistoryForCustodian = (sectionId, custodianId, costBasisId) => {
  return dispatch => {
    dispatch(deleteCustodianHistoryAction(costBasisId, true));
    const request = idempotentId =>
      ApiClient.deleteCostBasisHistoryForCustodian(idempotentId, sectionId, custodianId, costBasisId, history);
    const syncItem = new SyncItem(SyncItemType.UPDATE, history.id, request, 1000, true, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

const preFetchCustodianChartInFlightMap = new Map();
export const getCustodianChart = (
  custodianId,
  onSuccess = () => {},
  onError = () => {},
  fetchFromCache = false,
  preFetchOnly = false
) => {
  return (dispatch, getState) => {
    if (!preFetchOnly) {
      dispatch(getCustodianChartAction());
    }

    const fetchChart = (cachedApiData = null) => {
      const netWorthChartStartDate = getNetWorthChartStartDateForPortfolio(getState());
      const netWorthChartStartTime = netWorthChartStartDate.getTime();
      let promise = cachedApiData
        ? new Promise(resolve => {
            resolve(cachedApiData.chartApiData);
          })
        : !preFetchOnly && preFetchCustodianChartInFlightMap.has(custodianId)
        ? preFetchCustodianChartInFlightMap.get(custodianId)
        : ApiClient.getCustodianChart(getUuid(), custodianId);

      if (preFetchOnly) {
        preFetchCustodianChartInFlightMap.set(custodianId, promise);
      }

      promise
        .then(apiData => {
          if (!cachedApiData) {
            saveCustodianChartDataToCache(custodianId, apiData);
          }
          if (preFetchOnly) {
            preFetchCustodianChartInFlightMap.delete(custodianId);
            return;
          }

          let chartData = apiData.payload;
          chartData.chart = getChartDataFromResponse(chartData.chart, "data", false, false, netWorthChartStartDate);
          if (!chartData === false) {
            for (const chartDuration in chartData.chart) {
              if (chartDuration === chartTimeRange.LAST_YEAR_END) {
                continue;
              }

              chartData.chart[chartDuration].data =
                chartData.chart[chartDuration].data &&
                chartData.chart[chartDuration].data.filter(
                  dataPoint => getDateInKuberaFormat(dataPoint.date).getTime() >= netWorthChartStartTime
                );
            }
          }
          chartData = filterChartDataBasedOnPortfolioStartDate(chartData);
          const ytdCustodianChartData = getYTDCustodianChartData(
            chartData,
            chartData.chart[chartTimeRange.LAST_YEAR_END],
            netWorthChartStartDate
          );
          chartData.chart[chartTimeRange.YTD] = ytdCustodianChartData;
          dispatch(getCustodianChartSuccessAction(chartData));
          onSuccess(chartData);
        })
        .catch(apiError => {
          dispatch(getCustodianChartErrorAction(apiError));
          onSuccess(apiError);
        });
    };

    if (fetchFromCache) {
      const cachedApiData = getCustodianChartDataFromCache(custodianId);
      if (cachedApiData) {
        fetchChart(cachedApiData);
      }
    }
    fetchChart();
  };
};

export const fetchTickerDetails = (inputText, forDate, onSuccess, onError, parseInputText = true) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const portfolioTicker = getTickerUsingShortName(getState(), currentPortfolio.currency);

    dispatch(
      getTickersForText(
        inputText,
        tickers => {
          for (const tickerInfo of tickers) {
            dispatch(addTickerInfoAction(tickerInfo.info, tickerInfo.rate, forDate));
          }

          if (tickers.length > 0) {
            // this function is used within the details component. cost basis/cash flow is more likely to be entered in fiat/crypto so backend sort order is overrided
            const sortingCriteria = {
              fiat: 1,
              crypto: 2
            };

            const sortedTickers = tickers.sort((a, b) => {
              const typeA = a.info.type;
              const typeB = b.info.type;

              const priorityA = sortingCriteria[typeA] || Infinity;
              const priorityB = sortingCriteria[typeB] || Infinity;

              return priorityA === priorityB ? 0 : priorityA - priorityB;
            });
            const tickerInfo = sortedTickers[0].info;
            const forCurrentDate =
              getCustodianHistoryApiFormattedDateString(toUserTimezoneDate()) ===
              getCustodianHistoryApiFormattedDateString((forDate || toUserTimezoneDate()).getTime());
            const rate = forCurrentDate
              ? getExchangeRate(getState(), tickerInfo.shortName, currentPortfolio.currency, false)
              : getExchangeRate(getState(), tickerInfo.shortName, currentPortfolio.currency, false, forDate);
            const exchangeRate = forCurrentDate
              ? getExchangeRateDetails(portfolioTicker.id, rate)
              : getExchangeRateDetails(portfolioTicker.id, rate, forDate);

            const result = {
              exchangeRateDetails: exchangeRate,
              tickerId: tickerInfo.id,
              tickerShortName: tickerInfo.shortName,
              tickers: sortedTickers,
              exchangeRate: rate
            };

            onSuccess(result);
          } else {
            onSuccess(null);
          }
        },
        apiError => {
          if (apiError.errorCode === apiErrorCodes.INVALID_INPUT) {
            return onSuccess(null);
          }
          return onError(apiError);
        },
        parseInputText,
        forDate,
        currentPortfolio.currency
      )
    );
  };
};

export const updateCustodianCashflow = (
  isFirstEdit,
  custodianId,
  propertiesToUpdate,
  onSuccess = () => {},
  onError = error => {}
) => {
  return (dispatch, getState) => {
    if (propertiesToUpdate) {
      const custodian = custodianSelector(getState(), custodianId);
      dispatch(setSectionUpdated(custodian.sectionId));

      propertiesToUpdate.date = converUIDateFormatToCustodianHistoryApiDateFormat(propertiesToUpdate.date);

      if (!propertiesToUpdate.date) {
        return;
      }

      if (propertiesToUpdate.cashInExchangeRate && propertiesToUpdate.date) {
        try {
          const rateDetails = JSON.parse(propertiesToUpdate.cashInExchangeRate);
          if (rateDetails.date !== propertiesToUpdate.date) {
            rateDetails.date = propertiesToUpdate.date;
            propertiesToUpdate.cashInExchangeRate = JSON.stringify(rateDetails);
          }
        } catch (e) {}
      }

      if (propertiesToUpdate.cashOutExchangeRate && propertiesToUpdate.date) {
        try {
          const rateDetails = JSON.parse(propertiesToUpdate.cashOutExchangeRate);
          if (rateDetails.date !== propertiesToUpdate.date) {
            rateDetails.date = propertiesToUpdate.date;
            propertiesToUpdate.cashOutExchangeRate = JSON.stringify(rateDetails);
          }
        } catch (e) {}
      }

      custodian.isIrrUpdating = true;
      const currentPortfolio = currentPortfolioSelector(getState());
      dispatch(updateCustodianInBulkAction(currentPortfolio.id, [custodian]));
      dispatch(updateDashboardAction([custodian.id]));

      const custodianDetails = custodianDetailsSelector(getState());
      const matchingCashflow = custodianDetails
        ? custodianDetails.cashFlow.find(cashflowEntry => cashflowEntry.id === propertiesToUpdate.id)
        : null;
      const apiKeys = CUSTODIAN_CASHFLOW_API_KEYS;
      // Properties for which values changed will be added to this set
      // Used:
      // * For checking if only notes is updated in cashflow table then to prevent UI animation of IRR being recalculated
      const changedProperties = new Set();
      const apiObject = Object.keys(propertiesToUpdate)
        .filter(key => {
          const includesInApiKeys = apiKeys.includes(key);

          if (includesInApiKeys && matchingCashflow && matchingCashflow[key] !== propertiesToUpdate[key]) {
            changedProperties.add(key);
          }
          return includesInApiKeys;
        })
        .reduce((obj, key) => {
          obj[key] = propertiesToUpdate[key];
          return obj;
        }, {});

      if (custodianDetails && custodianDetails.info.id === custodianId) {
        dispatch(updateCustodianCashflowAction(propertiesToUpdate));
      }
      if (!(changedProperties.size === 1 && changedProperties.has("note"))) {
        dispatch(addPendingIrrRequestAction(apiObject.id));
      }

      if (isFirstEdit === true) {
        const request = idempotentId => ApiClient.createCustodianCashflow(idempotentId, custodianId, apiObject);
        const syncItem = new SyncItem(
          SyncItemType.CREATE,
          propertiesToUpdate.id,
          request,
          0,
          false,
          apiData => {
            const { irrType } = propertiesToUpdate;
            dispatch(
              updateCustodianDueToCashflowUpdate(
                { ...(irrType ? { ...apiData.payload, irrType } : apiData.payload), isIrrUpdating: false },
                apiObject.id
              )
            );
            onSuccess();
          },
          error => {
            onError(error);
          }
        );
        dispatch(enqueueItem(syncItem));
      } else {
        const request = idempotentId =>
          ApiClient.updateCustodianCashflow(idempotentId, custodianId, apiObject.id, apiObject);
        const syncItem = new SyncItem(
          SyncItemType.UPDATE,
          propertiesToUpdate.id,
          request,
          1000,
          true,
          apiData => {
            dispatch(updateCustodianDueToCashflowUpdate({ ...apiData.payload, isIrrUpdating: false }, apiObject.id));
            onSuccess();
          },
          error => {
            onError(error);
          }
        );
        dispatch(enqueueItem(syncItem));
      }
    }
  };
};

export const deleteCustodianCashflow = (custodianId, cashflowId) => {
  return dispatch => {
    dispatch(deleteCustodianCashflowAction(cashflowId));
    dispatch(addPendingIrrRequestAction(cashflowId));

    const request = idempotentId => ApiClient.deleteCustodianCashflow(idempotentId, custodianId, cashflowId);
    const syncItem = new SyncItem(SyncItemType.DELETE, cashflowId, request, 0, false, apiData => {
      dispatch(updateCustodianDueToCashflowUpdate(apiData.payload, cashflowId));
    });
    dispatch(enqueueItem(syncItem));
  };
};

export const deleteAllCustodianCashflows = custodianId => {
  return (dispatch, getState) => {
    const custodianDetails = custodianDetailsSelector(getState());
    custodianDetails.cashFlow = [];
    custodianDetails.info.irr = null;
    dispatch(updateCustodianDetailsAction(custodianDetails));

    const request = idempotentId => ApiClient.deleteAllCustodianCashflows(idempotentId, custodianId);
    const syncItem = new SyncItem(SyncItemType.DELETE, custodianId, request, 0, false, apiData => {
      dispatch(updateCustodianDueToCashflowUpdate(apiData.payload));
    });
    dispatch(enqueueItem(syncItem));
  };
};

export const deleteAllFundSchedules = (custodianId, type) => {
  return (dispatch, getState) => {
    const custodianDetails = custodianDetailsSelector(getState());
    custodianDetails.fundSchedule = (custodianDetails.fundSchedule || []).filter(item => item.type !== type);
    dispatch(updateCustodianDetailsAction(custodianDetails));

    const request = idempotentId => ApiClient.deleteAllCustodianFundSchedule(idempotentId, custodianId, type);
    const syncItem = new SyncItem(SyncItemType.DELETE, custodianId + type, request, 0, false, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

export const updateCustodianDueToCashflowUpdate = (updatedCustodian, cashflowId = undefined) => {
  return (dispatch, getState) => {
    const custodianDetails = custodianDetailsSelector(getState());
    if (custodianDetails && updatedCustodian.id === custodianDetails.info.id) {
      custodianDetails.info = updatedCustodian;
      dispatch(updateCustodianDetailsAction(custodianDetails));
    }
    dispatch(removePendingIrrRequestAction(cashflowId));

    const currentPortfolio = currentPortfolioSelector(getState());
    const currentCustodian = custodianSelector(getState(), updatedCustodian.id);
    dispatch(updateCustodianInBulkAction(currentPortfolio.id, [updatedCustodian]));
    if (getIrrValue(currentCustodian.irr, true) !== getIrrValue(updatedCustodian.irr, true)) {
      dispatch(updateSectionIrr([updatedCustodian.sectionId]));
    }
    dispatch(updateDashboardAction([updatedCustodian.id]));
  };
};

export function getOwnershipValueForCustodian(state, custodianId) {
  const custodian = custodianSelector(state, custodianId);
  if (!custodian) {
    return null;
  }
  if (!custodian.parentId === false) {
    const parentCustodian = custodianSelector(state, custodian.parentId);
    return parentCustodian ? parentCustodian.ownership : null;
  }

  return !custodian.ownership === false || custodian.ownership === 0 ? custodian.ownership : 100;
}

export const updateCustodianIrrType = (
  custodianId,
  irrType,
  forceUpdate = false,
  updateCustodian = true,
  resetIrrData = true
) => {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      const custodianDetails = custodianDetailsSelector(getState());
      if (custodianDetails) {
        if (resetIrrData) {
          custodianDetails.info.irr = null;
        }
        custodianDetails.info.irrType = irrType;
        dispatch(updateCustodianDetailsAction(custodianDetails));
      }

      const currentPortfolio = currentPortfolioSelector(getState());
      const updatedCustodian = custodianSelector(getState(), custodianId);
      const previousIrr = updatedCustodian.irr;
      updatedCustodian.irr = null;
      updatedCustodian.irrType = irrType;
      updatedCustodian.previousIrr = previousIrr;
      dispatch(updateCustodianInBulkAction(currentPortfolio.id, [updatedCustodian]));
      dispatch(updateDashboardAction([custodianId]));

      if (forceUpdate === false && irrType === irrTypes.COSTBASIS && !updatedCustodian.cost === true) {
        return;
      }
      if (forceUpdate === false && irrType === irrTypes.CASHFLOW && custodianDetails.cashFlow.length === 0) {
        return;
      }

      const request = idempotentId => ApiClient.updateCustodianIrrType(idempotentId, custodianId, irrType);
      const syncItem = new SyncItem(SyncItemType.UPDATE, "irr" + custodianId, request, 0, false, apiData => {
        if (updateCustodian) {
          dispatch(updateCustodianDueToCashflowUpdate(apiData.payload));
        }
        resolve(apiData.payload);
      });
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const updateCustodianHolding = (holding, propertiesToUpdate) => {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      const currentPortfolio = currentPortfolioSelector(getState());
      const updatedHolding = { ...holding, ...propertiesToUpdate };
      dispatch(insertCustodianInBulkAction(currentPortfolio.id, [updatedHolding]));

      const custodianDetails = custodianDetailsSelector(getState());
      if (custodianDetails?.holdings) {
        const holdingIndex = custodianDetails.holdings.findIndex(item => item.id === holding.id);
        if (holdingIndex !== -1) {
          custodianDetails.holdings[holdingIndex] = updatedHolding;
        }
      }
      dispatch(updateCustodianDetailsAction(custodianDetails));

      dispatch(
        updateCustodian(false, holding.id, propertiesToUpdate, false, () => {
          resolve(updatedHolding);
        })
      );
    });
  };
};

export const updateFundSchedule = (
  isFirstEdit,
  custodianId,
  fundSchedule,
  onSuccess = () => {},
  onError = () => {}
) => {
  return (dispatch, getState) => {
    if (fundSchedule) {
      fundSchedule.date = converUIDateFormatToCustodianHistoryApiDateFormat(fundSchedule.date);

      if (!fundSchedule.date) {
        return;
      }

      const apiKeys = FUND_SCHEDULE_API_KEYS;
      const apiObject = Object.keys(fundSchedule)
        .filter(key => apiKeys.includes(key) && fundSchedule[key] !== null)
        .reduce((obj, key) => {
          obj[key] = fundSchedule[key];
          return obj;
        }, {});

      const custodianDetails = custodianDetailsSelector(getState());
      if (custodianDetails && custodianDetails.info.id === custodianId) {
        dispatch(updateFundScheduleAction(apiObject));
      }

      if (isFirstEdit === true) {
        const request = idempotentId => ApiClient.createCustodianFundSchedule(idempotentId, custodianId, apiObject);
        const syncItem = new SyncItem(
          SyncItemType.CREATE,
          fundSchedule.id,
          request,
          0,
          false,
          apiData => {
            onSuccess();
          },
          error => {
            onError();
          }
        );
        dispatch(enqueueItem(syncItem));
      } else {
        const request = idempotentId =>
          ApiClient.updateCustodianFundSchedule(idempotentId, custodianId, fundSchedule.id, apiObject);
        const syncItem = new SyncItem(
          SyncItemType.UPDATE,
          fundSchedule.id,
          request,
          1000,
          true,
          apiData => {
            onSuccess();
          },
          error => {
            onError();
          }
        );
        dispatch(enqueueItem(syncItem));
      }
    }
  };
};

export const deleteFundSchedule = (custodianId, fundScheduleId) => {
  return dispatch => {
    dispatch(deleteFundScheduleAction(fundScheduleId));

    const request = idempotentId => ApiClient.deleteCustodianFundSchedule(idempotentId, custodianId, fundScheduleId);
    const syncItem = new SyncItem(SyncItemType.DELETE, fundScheduleId, request, 0, false, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

export const moveFundScheduleToCashflow = (type, custodianId, fundScheduleId, onSuccess, onError) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const portfolioTicker = getTickerUsingShortName(getState(), currentPortfolio.currency);
    const fundSchedules = fundScheduleSelector(getState(), type);
    const fundSchedule = fundSchedules.find(fundSchedule => fundSchedule.id === fundScheduleId);

    if (!fundSchedule) {
      return onError("Fund schedule not found");
    }

    const fundScheduleCurrency = getTickerUsingId(getState(), fundSchedule.valueTickerId).shortName;
    const fundScheduleDate = parseKuberaDateString(fundSchedule.date);
    const exchangeRate = getExchangeRate(
      getState(),
      fundScheduleCurrency,
      currentPortfolio.currency,
      false,
      fundScheduleDate
    );

    const moveToCashflow = () => {
      const exchangeRate = getExchangeRate(
        getState(),
        fundScheduleCurrency,
        currentPortfolio.currency,
        false,
        fundScheduleDate
      );
      const currentCashflows = custodianDetailsSelector(getState()).cashFlow;
      const cashflowWithSameDate = currentCashflows.find(item => item.date === fundSchedule.date);

      const propertiesToUpdate = cashflowWithSameDate
        ? { ...cashflowWithSameDate }
        : {
            id: fundSchedule.id,
            date: fundSchedule.date,
            note: fundSchedule.note
          };

      propertiesToUpdate.date = getCustodianHistoryFormattedDateString(
        parseKuberaDateString(propertiesToUpdate.date).getTime()
      );

      if (type === fundScheduleTypes.DISTRIBUTION) {
        propertiesToUpdate.cashOut = fundSchedule.value;
        propertiesToUpdate.cashOutTickerId = fundSchedule.valueTickerId;
        propertiesToUpdate.cashOutExchangeRate = getExchangeRateDetails(
          portfolioTicker.id,
          exchangeRate,
          fundScheduleDate
        );
      } else if (type === fundScheduleTypes.CAPITAL_CALL) {
        propertiesToUpdate.cashIn = fundSchedule.value;
        propertiesToUpdate.cashInTickerId = fundSchedule.valueTickerId;
        propertiesToUpdate.cashInExchangeRate = getExchangeRateDetails(
          portfolioTicker.id,
          exchangeRate,
          fundScheduleDate
        );
      }

      dispatch(
        updateCustodianCashflow(
          !cashflowWithSameDate === true,
          custodianId,
          propertiesToUpdate,
          () => {
            dispatch(deleteFundSchedule(custodianId, fundScheduleId));
            onSuccess();
          },
          error => {
            onError(error.errorMessage);
          }
        )
      );
    };

    if (!exchangeRate) {
      dispatch(
        fetchTickerDetails(
          fundScheduleCurrency,
          fundScheduleDate,
          () => {
            moveToCashflow();
          },
          error => onError(error),
          false
        )
      );
    } else {
      moveToCashflow();
    }
  };
};

export const checkIfACustodianIsCash = custodian => {
  return custodian.type === 2;
};

// Usecase: When custodian is updating and details should be fetched after the custodian is updated
// Added for PVST to manual and vice-versa
// If someone clicks on details immediately on doing above actions it should only fetch details after the above actions are completed
let detailsBlockPromise = new DeferredPromise();
detailsBlockPromise.resolve();

export const getDetailsBlockPromise = () => detailsBlockPromise;

let detailsBlockTimeoutId = null;
export const blockDetailsLoading = () => {
  detailsBlockPromise = new DeferredPromise();

  clearTimeout(detailsBlockTimeoutId);
  detailsBlockTimeoutId = setTimeout(() => {
    detailsBlockPromise.resolve();
  }, 5000);
};

export const unblockDetailsLoading = () => {
  detailsBlockPromise.resolve();
};

// this function is to get asset class from custodian fundamental data,fundamental data has keyss different from custodian details

////////////////////////////////////////
export const updateCustodianAutopilotRules = (custodianId, rules) => {
  return (dispatch, getState) => {
    dispatch(updateCustodian(false, custodianId, { autopilotRules: JSON.stringify(rules) }, true));
  };
};

const eventMethods = { updateCustodianHolding };

eventBus.addEventListener("any", event => {
  const { eventName, data } = event.detail;
  if (eventMethods[eventName]) {
    store.dispatch(eventMethods[eventName].apply(this, data));
  }
});
